import { useMemo } from 'react';
import { FileText } from 'lucide-react';
import { ENTITY_ICONS, orgFunctionKindIconComponent } from '@/constants/entityIcons';
import {
  getUserAvatarUrl,
  getUserFullName,
  getUserInitials,
  resolveUserLifecycle
} from '@bliks-io/ui';
import type {
  Artifact,
  OrgFunction,
  OrgMemberListItem,
  SystemWithCapacity
} from '@bliks-io/contracts';
import { useOrgMembersForLookup } from '@/api/queries/useOrgMembers';
import { useOrgFunctionsForLookup } from '@/api/queries/useOrgFunctions';
import { useSystemsForLookup } from '@/api/queries/useSystems';
import { useArtifacts } from '@/api/queries/useArtifacts';
import { getLogoUrl } from '@/pages/organization/systems/components/graph-3d-utils';
import type { EntityListItem } from './EntityPickerList';

interface EntityPreview {
  items: EntityListItem[];
  /** Stored ids still waiting on their lookup query; a failed lookup counts 0. */
  loadingCount: number;
}

function usePreview<T extends { _id: string }>(
  query: { data?: T[]; isLoading: boolean },
  ids: string[],
  toItem: (entity: T) => EntityListItem
): EntityPreview {
  const { data, isLoading } = query;
  const items = useMemo(() => {
    if (!data) return [];
    const byId = new Map(data.map((entity) => [entity._id, entity]));
    return ids
      .map((id) => byId.get(id))
      .filter((entity): entity is T => Boolean(entity))
      .map(toItem);
  }, [data, ids, toItem]);
  return { items, loadingCount: isLoading ? ids.length : 0 };
}

function personToItem(u: OrgMemberListItem): EntityListItem {
  return {
    key: u._id,
    label: getUserFullName(u.firstName, u.lastName),
    imageUrl: getUserAvatarUrl(u, 'thumbnail'),
    initials: getUserInitials(u.firstName, u.lastName),
    isDeleted: Boolean(u.deletedAt),
    lifecycle: resolveUserLifecycle(u)
  };
}

function orgFunctionToItem(fn: OrgFunction): EntityListItem {
  const KindIcon = orgFunctionKindIconComponent(fn.kind);
  return {
    key: fn._id,
    label: fn.name,
    fallbackIcon: <KindIcon className="h-3 w-3" />,
    isDeleted: Boolean(fn.deletedAt)
  };
}

function systemToItem(s: SystemWithCapacity): EntityListItem {
  return {
    key: s._id,
    label: s.name,
    imageUrl: getLogoUrl(s.logo),
    fallbackIcon: <ENTITY_ICONS.system className="h-3 w-3" />,
    isDeleted: Boolean(s.deletedAt)
  };
}

function artifactToItem(a: Artifact): EntityListItem {
  return {
    key: a._id,
    label: a.name,
    fallbackIcon: <FileText className="h-3 w-3" />
  };
}

export function usePersonPreviewItems(ids: string[]): EntityPreview {
  return usePreview(useOrgMembersForLookup(), ids, personToItem);
}

export function useOrgFunctionPreviewItems(ids: string[]): EntityPreview {
  return usePreview(useOrgFunctionsForLookup(), ids, orgFunctionToItem);
}

export function useSystemPreviewItems(ids: string[]): EntityPreview {
  return usePreview(useSystemsForLookup(), ids, systemToItem);
}

export function useArtifactPreviewItems(ids: string[]): EntityPreview {
  return usePreview(useArtifacts(), ids, artifactToItem);
}
